import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  MeetingNoteTag,
  useCreateMeetingNoteTag,
} from "@/hooks/useMeetingNotes";

interface MeetingNoteTagPickerProps {
  tags: MeetingNoteTag[];
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
}

const TAG_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
];

export function MeetingNoteTagPicker({
  tags,
  selectedTagIds,
  onChange,
}: MeetingNoteTagPickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const createTag = useCreateMeetingNoteTag();

  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id));

  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const handleCreateTag = async () => {
    if (!inputValue.trim()) return;

    const color = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
    const result = await createTag.mutateAsync({
      name: inputValue.trim(),
      color,
    });

    if (result) {
      onChange([...selectedTagIds, result.id]);
      setInputValue("");
    }
  };

  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const showCreateOption =
    inputValue.trim() &&
    !filteredTags.some(
      (tag) => tag.name.toLowerCase() === inputValue.toLowerCase(),
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          size="sm"
          className="h-7 justify-between"
          data-testid="tag-picker-trigger"
        >
          {selectedTags.length > 0 ? (
            <div className="flex items-center gap-1">
              {selectedTags.slice(0, 2).map((tag) => (
                <Badge
                  key={tag.id}
                  variant="secondary"
                  style={{ backgroundColor: tag.color || undefined }}
                  className={cn("text-xs", tag.color && "text-white")}
                >
                  {tag.name}
                </Badge>
              ))}
              {selectedTags.length > 2 && (
                <span className="text-xs text-muted-foreground">
                  +{selectedTags.length - 2}
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">Tags</span>
          )}
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search or create tag..."
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            <CommandEmpty>
              {showCreateOption ? null : "No tags found."}
            </CommandEmpty>
            <CommandGroup>
              {filteredTags.map((tag) => (
                <CommandItem
                  key={tag.id}
                  value={tag.name}
                  onSelect={() => toggleTag(tag.id)}
                >
                  <div
                    className="h-3 w-3 rounded-full mr-2"
                    style={{ backgroundColor: tag.color || "#888" }}
                  />
                  {tag.name}
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      selectedTagIds.includes(tag.id)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
              {showCreateOption && (
                <CommandItem onSelect={handleCreateTag}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create "{inputValue}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
